import React, { useContext, SyntheticEvent, FunctionComponent, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import Toolbar from "@mui/material/Toolbar";

//MUI Icons
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";

//Custom Components
import api from "@/lib/api";
import useFormController from "@/components/useFormController";
import DynamicDialog from "@/ui/Dialog/DynamicDialog";
import DialogHeader from "@/ui/Dialog/DialogHeader";
import DialogFooter from "@/ui/Dialog/DialogFooter";
import DialogToolbarHeading from "@/ui/Dialog/DialogToolbarHeading";
import DialogToolbarButtonClose from "@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import DialogBody from "@/ui/Dialog/DialogBody";
import DialogActionButtonClose from "@/ui/Dialog/ActionButtons/DialogActionButtonClose";
import DialogActionButtonAdd from "@/ui/Dialog/ActionButtons/DialogActionButtonAdd";
import DialogActionButtonSave from "@/ui/Dialog/ActionButtons/DialogActionButtonSave";
import DialogToolbarMenuButton from "@/ui/Dialog/DialogToolbarMenuButton";
import DialogToolbarMenuItem from "@/ui/Dialog/DialogToolbarMenuItem";
import DialogContext from "@/ui/DialogContext/DialogContext";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import UserContext from "@/components/UserContext/UserContext";
import { GridContainer, GridItem } from "@/ui/Grid";
import FormContent from "@/components/FormContent";
import { Permissions } from "@/lib/permissions";
import { sifrarnici } from "@/lib/sifrarnici";
import FeatureMap from "@/views/GIS/MapPage/FeatureMap";
import FormAvatar from "@/ui/Form/FormAvatar";
import FormHeading from "@/ui/Form/FormHeading";
import FormHeadingExtendedData from "@/ui/Form/FormHeadingExtendedData";

//Custom Icons
import POIIcon from "@/icons/POIIcon";
import POIIconVrh from "@/icons/POIIconVrh";
import POIIconKuca from "@/icons/POIIconKuca";

//Types
import { ClosingDetails, FormControllerProps } from "@/@types/components/formController";
import { IDialogProps } from "@/@types/ui/DialogContext";
import { OwnerOrganisations } from "@/lib/ownerOrganisations";
import { DCFieldValue } from "@/@types/lib/dataController";
import { PickerItem } from "@/@types/controls/controls";

const POIDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const [isEditingGeometry, setIsEditingGeometry] = useState(false);

  const { dc, mode, form, onClose, preClose } = props;

  const { record, validation, dataChanged, fields, subModels, onFieldChange, doInsert, doUpdate, doClose, doRefresh } =
    useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.insert"), "success");
          close({ dataChanged: true, action: "insert" });
        } else if (result.error) {
          const msg = t("error.addRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      })
        .catch((resp) => {
          snackbarContext.showNotification(resp.error, "error");
        });
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate().then((result) => {
        if (result.success) {
          snackbarContext.showNotification(t("notifications.update"), "success");
          close({ dataChanged: true, action: "update" });
        } else if (result.error) {
          const msg = t("error.editRecord") + result.error;
          snackbarContext.showNotification(msg, "error");
        } else if (result.validationPass === false) {
          //do nothing
        }
      });
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `poi/${record.id}/activate`
        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.activate_record_success");
            snackbarContext.showNotification(msg, "success");
            doRefresh();
            onClose({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.activate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.activate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        const apiInstance = new api();
        const url = `poi/${record.id}/deactivate`

        apiInstance.Call(url, "patch").then((result) => {
          if (result) {
            const msg = t("messages.deactivate_record_success");
            snackbarContext.showNotification(msg, "success");
            close({ dataChanged: true, action: "update" });
          } else {
            const msg = t("error.deactiavate_record");
            snackbarContext.showNotification(msg, "error");
          }
        });
      } catch (error) {
        const msg = t("error.deactivate_record");
        snackbarContext.showNotification(msg, "error");
      }
    }
  };

  const handleTurnOnGeometryChange = (evt: SyntheticEvent) => {
    setIsEditingGeometry(true);
  }

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose().then((result) => {
        if (result.success) {
          close({ dataChanged: false, action: "cancel" });
        } else {
          if (result.shouldSave) {
            handleUpdate(evt);
          } else if (result.canceled) {
            //do nothing
          }
        }
      }).catch(result => {console.log(result)});
    }
  };

  const handleGeometryChange = useCallback((wkt: string) => {
    onFieldChange(wkt, "wkt");
    onFieldChange('EPSG:3765', 'proj');
    if (mode === "update" && isEditingGeometry) {
      onFieldChange(1, "geom_changed")
    }
  }, [mode, isEditingGeometry]);

  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged) {
      onClose({ dataChanged: true, action: result.action });
      if (preClose) {
        preClose(record);
      }
    }

    dialogContext.hideDialog();
  };

  const getIcon = (tip_id: number | DCFieldValue) => {
    const tip_value = typeof tip_id === "object" && tip_id?.hasOwnProperty("value") ? (tip_id as PickerItem).value : tip_id;

    switch (tip_value) {
      case sifrarnici.poi_tipovi.planinarska_kuca:
      case sifrarnici.poi_tipovi.planinarski_dom:
      case sifrarnici.poi_tipovi.planinarsko_skloniste:
        return <POIIconKuca />;
      case sifrarnici.poi_tipovi.vrh:
        return <POIIconVrh />
      default:
        return <POIIcon />
    }
  }

  const title = record.naziv ? (record.naziv as string + (record.active === false ? ` - ${t('common.deactivated_record')}` : '')) : t("titles.poi_1") as string;
  const disableSave = dataChanged ? false : true;

  const recordOwnerId = record.owner_organisation as number; // 20 za hgss, 30 za hps

  const allowActivation = mode === "update" && ((userContext?.hasAnyPermission([Permissions.HGSSMod]) && recordOwnerId === OwnerOrganisations.HGSS.code)
    || (userContext?.hasAnyPermission([Permissions.HPSMod]) && recordOwnerId === OwnerOrganisations.HPS.code))

  const allowGeometryChange = mode === "update" && userContext?.hasAnyPermission([Permissions.Administration]) && recordOwnerId === OwnerOrganisations.HGSS.code;

  const hasToolbarActions = allowActivation || allowGeometryChange;

  const icon = getIcon(record?.poi_tip_id);

  let cmode = mode;
  if (record.active === false) {
    cmode = "view";
  }  

  const mapMode = cmode === "insert" ? "insert"
  : cmode === "update" ? (isEditingGeometry ? "update" : "view")
    : "view"

  const turnedOnGeometryChange = mapMode === "insert" || mapMode === "update";

  return (
    <DynamicDialog open={true} maxWidth={"lg"} fixedHeight={true} onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters={true}>
          <FormAvatar icon={icon} ariaLabel="poi" />
          <FormHeading header={title} subheaderContent={record.id ? <FormHeadingExtendedData record={record} /> : undefined} />
          <ToolbarFillContent />
          {hasToolbarActions ? (
            <DialogToolbarMenuButton>
              {mode === "update" && allowActivation && record.active === true ? (
                <DialogToolbarMenuItem
                  onClick={handleDeactivate}
                  label={t("buttons.deactivate")}
                  icon={<CancelIcon />}
                />
              ) : null}
              {mode === "update" && allowActivation && record.active === false ? (
                <DialogToolbarMenuItem
                  onClick={handleActivate}
                  label={t("buttons.activate")}
                  icon={<CheckCircleIcon />}
                />
              ) : null}
              {mode === "update" && allowGeometryChange && record.active === true ? (
                <DialogToolbarMenuItem
                onClick={handleTurnOnGeometryChange}
                label={t("buttons.update_geom")}
                icon={<EditLocationAltIcon/>}
                />

              ): null}
            </DialogToolbarMenuButton>

          ) : null}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            <GridItem size={{xs: 12, sm: 6}}>
              <GridContainer>
                <FormContent
                  fieldNames={dc.getFormFieldsNames(form).filter((x: string) => x !== "id")}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={handleFieldChange}
                  mode={cmode}
                  columns={2}
                />
              </GridContainer>
            </GridItem>
            <GridItem size={{xs: 12, sm: 6}}>
              {record ?
                <FeatureMap
                  wkt={record.wkt as string}
                  proj={record.proj as string}
                  mode={mapMode}
                  onGeometryChange={turnedOnGeometryChange ? handleGeometryChange : undefined}
                />
                : null}
            </GridItem>
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogFooter record={record}>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === "insert" ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : null}
        {mode === "update" ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={disableSave} />
        ) : null}
      </DialogFooter>
    </DynamicDialog>
  );
};

export default POIDialog;
